/* eslint-disable @typescript-eslint/no-explicit-any */
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import Tooltip from '@mui/material/Tooltip';
import { DatePicker, DatePickerProps } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import { FormikProps } from 'formik';
import fileDownload from 'js-file-download';
import React, { createRef, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Input } from 'reactstrap';

import { useModal, useOnClickOutside } from 'shared/hooks';
import { TaskKeysType, WorkType } from 'shared/types';
import { AudioPlayer, VideoPlayer } from 'shared/ui';

import { AudioInput, PdfInput, WordInput, VideoInput, FilesPopup } from '..';
import { audioApi, pdfApi, wordApi, videoApi } from '../../../api';
import { imgApi } from '../../../api/img-api';
import closeSvg from '../../../assets/img/close.svg';
import imageSvg from '../../../assets/img/image-rectangle.svg';
import paperClipSvg from '../../../assets/img/paper-clip.svg';
import trashSvg from '../../../assets/img/trash.svg';
import { SummarizeTask } from '../../../shared/types/task';
import { ItemActionModal } from '../../../shared/ui/ItemActionModal';
import BigPictureModal from '../../BigPictureModal';
import DocumentItem from '../DocumentItem';
import TaskDeadline from '../TaskDeadline';
import i18next from 'i18next';
import './index.scss';

type TaskWrapperProps = Partial<SummarizeTask> & {
  id: string;
  children: React.JSX.Element | React.JSX.Element[];
  handleDelete: (id: string) => Promise<void> | void;
  handleChangeTaskByKey: (id: string, key: TaskKeysType, value: string | boolean | string[]) => void;
  save: () => void | string | Promise<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formikProp: FormikProps<any>;
  updateICL: boolean;
  isDeleteButtonDisabled?: boolean;
  isSubmitButtonDisabled?: boolean;
  currentICLId: string;
  remoteTaskId?: string;
  workType?: WorkType;
  inputValues?: object;
  index: number;
};

import 'react-h5-audio-player/lib/styles.css';
import { useDispatch, useSelector } from 'react-redux';
import { setSavedTask } from 'redux/slices/flagsSlice';

export default function TaskWrapper({
  children,
  id,
  handleDelete,
  handleChangeTaskByKey,
  hasAnswer,
  hasStar,
  save,
  formikProp,
  isDeleteButtonDisabled,
  isSubmitButtonDisabled,
  hasPhoto,
  isActive,
  deadline,
  audioUrl,
  currentICLId,
  remoteTaskId,
  pdfUrl,
  wordUrl,
  videoUrl,
  taskPhotos,
  workType,
  inputValues,
  index,
}: TaskWrapperProps) {
  const { t } = useTranslation();

  const { isOpen: isFilesModalOpen, close: closeFilesModal, toggle: toggleFilesModal } = useModal();
  const { isOpen: isBigPictureOpen, close: bigPictureCLose, open: bigPictureOpen } = useModal();
  const { isOpen: isDeleteImgOpen, open: openDeleteImg, close: closeDeleteImg } = useModal();

  const dispatch = useDispatch();

  const imgInputRef = createRef<HTMLInputElement>();
  const filesModalRef = useOnClickOutside(closeFilesModal);

  const [imageForDelete, setImageForDelete] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [bigPicture, setBigPicture] = useState<string>('');
  const [imageFiles, setImageFiles] = useState<{ file: File; url: string }[]>([]);
  const [audioFile, setAudioFile] = useState<{ file: File; url: string } | null>(null);
  const [videoFile, setVideoFile] = useState<{ file: File; url: string } | null>(null);
  const [pdfFile, setPdfFile] = useState<{ file: File; url: string } | null>(null);
  const [wordFile, setWordFile] = useState<{ file: File; url: string } | null>(null);
  const taskBodyRef = useRef<HTMLDivElement | null>(null);
  const [toggleTask, setToggleTask] = useState(true);

  const isReadyToSaveExcludeTask = () => {
    if (inputValues !== undefined) {
      return Object.values(inputValues).filter(item => item !== '').length > 0;
    } else {
      return null;
    }
  };

  useEffect(() => {
    const taskBody = taskBodyRef.current;
    if (taskBody) {
      if (toggleTask) {
        taskBody.style.maxHeight = `100%`;
      } else {
        taskBody.style.maxHeight = '0';
      }
    }
  }, [toggleTask]);

  const handleToggleTask = () => {
    setToggleTask(prev => !prev);
  };

  const uploadAudio = async (remoteTaskId: string) => {
    if (remoteTaskId && audioFile) {
      const file = audioFile.file;
      const formData = new FormData();
      formData.append('file', file);

      try {
        const gottenAudioUrl = await audioApi.addAudio(file, currentICLId, id);
        toast.success(t('Successfully added!'));
        return gottenAudioUrl;
      } catch (error) {
        console.log(error);
        toast.error('Something went wrong. Try again!');
      }
    }
  };

  const uploadVideo = async (remoteTaskId: string) => {
    if (remoteTaskId && videoFile) {
      const file = videoFile.file;
      const formData = new FormData();
      formData.append('file', file);

      try {
        const gottenVideoUrl = await videoApi.addVideo(file, currentICLId, id);
        toast.success(t('Successfully added!'));

        return gottenVideoUrl;
      } catch (error) {
        console.log(error);
        toast.error('Something went wrong. Try again!');
      }
    }
  };

  const uploadPdf = async (remoteTaskId: string) => {
    if (remoteTaskId && pdfFile) {
      const file = pdfFile.file;
      const formData = new FormData();
      formData.append('file', file);

      try {
        const gottenPdfUrl = await pdfApi.addPdf(file, currentICLId, id);
        toast.success(t('Successfully added!'));

        return gottenPdfUrl;
      } catch (error) {
        console.log(error);
        toast.error('Something went wrong. Try again!');
      }
    }
  };

  const uploadWord = async (remoteTaskId: string) => {
    if (remoteTaskId && wordFile) {
      const file = wordFile.file;
      const formData = new FormData();
      formData.append('file', file);

      try {
        const gottenWordUrl = await wordApi.addWord(file, currentICLId, id);
        toast.success(t('Successfully added!'));

        return gottenWordUrl;
      } catch (error) {
        console.log(error);
        toast.error('Something went wrong. Try again!');
      }
    }
  };

  const addImagesToRemoteTask = (remoteTaskId: string) => {
    if (remoteTaskId && imageFiles.length) {
      setIsLoading(true);

      const promises = imageFiles.map(it => {
        const formData = new FormData();
        formData.append('file', it.file);

        return imgApi.addImg(it.file, currentICLId, id, taskPhotos || [], 'taskPhotos');
      });

      return Promise.all(promises)
        .then(gottenImageUrlArrs => {
          const allUrls = gottenImageUrlArrs.flat();
          imgApi.addImgArray(allUrls, currentICLId, id, 'taskPhotos').then(data => {});

          return allUrls;
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const handleSave = () => {
    dispatch(setSavedTask(true));
    handleToggleTask();

    setIsLoading(true);

    if (!remoteTaskId) {
      const createdTaskId = save() as string;

      if (createdTaskId) {
        const promises = [
          addImagesToRemoteTask(createdTaskId),
          uploadAudio(createdTaskId),
          uploadVideo(createdTaskId),
          uploadPdf(createdTaskId),
          uploadWord(createdTaskId),
        ];

        Promise.all(promises).then(([urls, audioUrl, videoUrl, pdfUrl, wordUrl]) => {
          if (Array.isArray(urls)) {
            setImageFiles(urls.map(url => ({ file: null, url })));
          }
          if (audioUrl) {
            setAudioFile({ file: null, url: audioUrl });
          }
          if (videoUrl) {
            setVideoFile({ file: null, url: videoUrl });
          }
          if (pdfUrl) {
            setPdfFile({ file: null, url: pdfUrl });
          }
          if (wordUrl) {
            setWordFile({ file: null, url: wordUrl });
          }
        });
      }
    } else {
      console.log('else is working saving task ');
      save();
    }

    setIsLoading(false);
  };

  const handleDeleteImage = async (img: string) => {
    setIsLoading(true);

    if (remoteTaskId) {
      try {
        const newPhotos = await imgApi.removeImg(img, currentICLId, id, taskPhotos || [], 'taskPhotos');

        handleChangeTaskByKey(id, 'taskPhotos', newPhotos);
        const newImageFiles = imageFiles.filter(it => it.url !== img);
        setImageFiles(newImageFiles);

        toast.success(t('Successfully removed!'));
        closeDeleteImg();
        setImageForDelete('');
      } catch (error) {
        console.log(error);
        toast.error(t('Something went wrong. Try again!'));
        closeDeleteImg();
        setImageForDelete('');
      }

      setIsLoading(false);
    } else {
      const newImageFiles = imageFiles.filter(it => it.url !== img);
      setImageFiles(newImageFiles);
      setIsLoading(false);
    }
  };

  const handleAddImageLocal = async () => {
    const imgInput = imgInputRef.current;

    if (!imgInput || !imgInput.files) {
      return;
    }

    const file = imgInput.files[0];
    const newImageFiles = [...imageFiles, { file: file, url: URL.createObjectURL(file) }];
    setImageFiles(newImageFiles);
    toast.success(t('Successfully added!'));
    imgInput.value = '';
  };

  const handleAddImage = async () => {
    const imgInput = imgInputRef.current;

    if (!imgInput || !imgInput.files) {
      return;
    }

    setIsLoading(true);
    const file = imgInput.files[0];
    const formData = new FormData();
    formData.append('file', file);

    try {
      const gottenImageUrlArr = await imgApi.addImg(file, currentICLId, id, taskPhotos || [], 'taskPhotos');
      handleChangeTaskByKey(id, 'taskPhotos', gottenImageUrlArr);

      toast.success(t('Successfully added!'));
    } catch (error) {
      console.log(error);
      toast.error(t('Something went wrong. Try again!'));
    }

    setIsLoading(false);
  };

  const renderImgButton = (photo: string) => {
    return (
      <button
        className="absolute right-[10px] top-[10px] cursor-pointer rounded-[6px] border-[1px] border-[#d1cec7] bg-white p-[3px]"
        onClick={e => {
          e.stopPropagation();
          setImageForDelete(photo);
          openDeleteImg();
        }}
      >
        <img src={closeSvg} alt={'close'} />
      </button>
    );
  };

  const renderPhotos = () => {
    if (taskPhotos) {
      return taskPhotos.length ? (
        <div className="relative overflow-x-auto">
          <div className="flex w-fit !gap-[12px]">
            {taskPhotos.map((it, index) => {
              return (
                <div
                  onClick={() => {
                    setBigPicture(it);
                    bigPictureOpen();
                  }}
                  className="relative h-[130px] w-[130px] overflow-hidden rounded-[10px] border-[2px] border-[#52C7FA]"
                  key={index}
                >
                  {renderImgButton(it)}
                  <img className="h-[150px] w-[150px]" src={it} alt={it} />
                </div>
              );
            })}
          </div>
        </div>
      ) : null;
    } else {
      return imageFiles.length ? (
        <div className="relative overflow-x-auto">
          <div className="flex w-fit !gap-[12px]">
            {imageFiles.map((it, index) => {
              return (
                <div
                  onClick={() => {
                    setBigPicture(it.url);
                    bigPictureOpen();
                  }}
                  className="relative h-[130px] w-[130px] overflow-hidden rounded-[10px] border-[2px] border-[#52C7FA]"
                  key={index}
                >
                  {renderImgButton(it.url)}
                  <img className="h-[150px] w-[150px]" src={it.url} alt={it.url} />
                </div>
              );
            })}
          </div>
        </div>
      ) : null;
    }
  };

  const handleChangeDeadlineDate: DatePickerProps['onChange'] = (_, dateStr) =>
    handleChangeTaskByKey(id, 'deadline', dateStr);

  const downloadFile = (fileUrl: string) => {
    const correction = fileUrl.includes('/Pdf') ? 7 : 8; // /Pdf or /Word
    const startingIndex = fileUrl.lastIndexOf('/') + correction;
    const endingIndex = fileUrl.lastIndexOf('?');
    const fileName = fileUrl.slice(startingIndex, endingIndex);

    axios
      .get(fileUrl, {
        responseType: 'blob',
      })
      .then(res => {
        fileDownload(res.data, fileName);
      });
  };

  return (
    <>
      <div>
        <div className="flex flex-col !gap-4 rounded-xl bg-[#f5f5f5] !px-5 !py-4 lg:!gap-5">
          <div className="taskHeader">
            <p className="taskNumber">
              {t('iclTask')} № {index + 1}
            </p>
            <button onClick={handleToggleTask} type="button">
              <span className={`icon-chevron-down chevron ${toggleTask ? 'rotate' : ''}`} />
            </button>
          </div>

          <div ref={taskBodyRef} className="task-body">
            {children}
          </div>
          {renderPhotos()}
          {(audioUrl || audioFile) && (
            <AudioPlayer src={audioUrl ?? (audioFile?.url as string)} className="lg:hidden" />
          )}
          {(videoUrl || videoFile) && (
            <VideoPlayer src={videoUrl ?? (videoFile?.url as string)} className="lg:hidden" />
          )}
          <div className="flex flex-col !gap-3 lg:flex-row lg:!gap-6 xl:!gap-8">
            <div
              className="flex cursor-pointer items-center !gap-[10px]"
              onClick={() => handleChangeTaskByKey(id, 'hasAnswer', !hasAnswer)}
            >
              <Input
                type="checkbox"
                className="mt-0 h-[20px] w-[20px] cursor-pointer border-2 border-[#4383F2] checked:border-[#4383F2] checked:bg-[#4383F2] checked:ring-[#4383F2] checked:ring-opacity-50 focus:border-[#4383F2] focus:ring-[#4383F2] focus:ring-opacity-50"
                onChange={e => handleChangeTaskByKey(id, 'hasAnswer', e.target.checked)}
                checked={hasAnswer}
              />

              <p className="!mb-0 text-base font-bold lg:text-lg">
                {hasAnswer ? t('taskWithAnswer') : t('taskWithoutAnswer')}
              </p>
            </div>

            <div
              className="flex cursor-pointer items-center !gap-[10px]"
              onClick={() => handleChangeTaskByKey(id, 'hasStar', !hasStar)}
            >
              <Input
                type="checkbox"
                className="mt-0 h-[20px] w-[20px] cursor-pointer border-2 border-[#4383F2] checked:border-[#4383F2] checked:bg-[#4383F2] checked:ring-[#4383F2] checked:ring-opacity-50 focus:border-[#4383F2] focus:ring-[#4383F2] focus:ring-opacity-50"
                onChange={e => handleChangeTaskByKey(id, 'hasStar', e.target.checked)}
                checked={hasStar}
              />
              <p className="!mb-0 text-base font-bold lg:text-lg">{t('addStar')}</p>
            </div>

            <div
              className="flex cursor-pointer items-center !gap-[10px]"
              onClick={() => handleChangeTaskByKey(id, 'hasPhoto', !hasPhoto)}
            >
              <Input
                type="checkbox"
                className="mt-0 h-[20px] w-[20px] cursor-pointer border-2 border-[#4383F2] checked:border-[#4383F2] checked:bg-[#4383F2] checked:ring-[#4383F2] checked:ring-opacity-50 focus:border-[#4383F2] focus:ring-[#4383F2] focus:ring-opacity-50"
                onChange={e => handleChangeTaskByKey(id, 'hasPhoto', e.target.checked)}
                checked={hasPhoto}
              />
              <p className="!mb-0 text-base font-bold lg:text-lg">{t('hasPhoto')}</p>
            </div>
          </div>
          <div className="flex flex-col justify-between lg:flex-row lg:items-center lg:justify-normal">
            <div className="!mb-4 lg:!mb-0 lg:mr-8">
              <DatePicker
                value={deadline ? dayjs(deadline) : undefined}
                placeholder={t('deadline')}
                onChange={handleChangeDeadlineDate}
                style={{ borderWidth: '1px', borderColor: '#D1CEC7', width: '152px', height: '40px' }}
              />
            </div>

            <div className="flex flex-col items-center !gap-4 lg:w-full lg:flex-row lg:!gap-3">
              <div className="flex w-full items-center justify-between">
                <div ref={filesModalRef} className="relative xl:hidden">
                  <Tooltip
                    title={t('add file')}
                    componentsProps={{
                      popper: {
                        sx: {
                          ['& .MuiTooltip-arrow']: {
                            color: '#9CDFFC',
                          },
                          ['& .MuiTooltip-tooltip']: {
                            backgroundColor: '#9CDFFC',
                            color: '#000000',
                            fontSize: '14px',
                            padding: '14px',
                            borderRadius: '8px',
                          },
                        },
                      },
                    }}
                    arrow
                  >
                    <button
                      className="btn more small h-[40px] w-[40px] rounded-[8px] !p-[6px]"
                      onClick={toggleFilesModal}
                    >
                      <img src={paperClipSvg} alt="image" />
                    </button>
                  </Tooltip>

                  <div
                    className={`absolute -left-2 bottom-12 z-50 flex w-[275px] flex-col gap-[20px] rounded-[10px] border-2 border-[#52C7FA] bg-white p-[30px] duration-300 ${isFilesModalOpen ? 'opacity-100' : 'invisible opacity-0'}`}
                  >
                    <VideoInput
                      currentICLId={currentICLId}
                      handleChangeTaskByKey={handleChangeTaskByKey}
                      id={id}
                      remoteTaskId={remoteTaskId}
                      videoUrl={videoUrl}
                      videoFile={videoFile}
                      setVideoFile={setVideoFile}
                      hasText
                      closeModal={closeFilesModal}
                    />

                    <Tooltip
                      title={t('add-photo')}
                      componentsProps={{
                        popper: {
                          sx: {
                            ['& .MuiTooltip-arrow']: {
                              color: '#9CDFFC',
                            },
                            ['& .MuiTooltip-tooltip']: {
                              backgroundColor: '#9CDFFC',
                              color: '#000000',
                              fontSize: '14px',
                              padding: '14px',
                              borderRadius: '8px',
                            },
                          },
                        },
                      }}
                      arrow
                    >
                      <button
                        className="flex items-center gap-[10px] rounded-[8px] !p-[0px]"
                        onClick={() => {
                          closeFilesModal();
                          imgInputRef.current?.click();
                        }}
                      >
                        <img src={imageSvg} alt="image" />

                        <input
                          className="hidden"
                          id="imageInput"
                          type="file"
                          ref={imgInputRef}
                          accept="image/*"
                          onChange={() => (remoteTaskId ? handleAddImage() : handleAddImageLocal())}
                        />

                        <p className="!mb-0 text-base font-bold text-[#242C2C]">{t('add-photo')}</p>
                      </button>
                    </Tooltip>

                    <AudioInput
                      currentICLId={currentICLId}
                      handleChangeTaskByKey={handleChangeTaskByKey}
                      id={id}
                      remoteTaskId={remoteTaskId}
                      audioUrl={audioUrl}
                      audioFile={audioFile}
                      setAudioFile={setAudioFile}
                      hasText
                      closeModal={closeFilesModal}
                    />

                    {workType === WorkType.hw && (
                      <>
                        <PdfInput
                          currentICLId={currentICLId}
                          handleChangeTaskByKey={handleChangeTaskByKey}
                          id={id}
                          remoteTaskId={remoteTaskId}
                          pdfUrl={pdfUrl}
                          pdfFile={pdfFile}
                          setPdfFile={setPdfFile}
                          hasText
                          closeModal={closeFilesModal}
                        />

                        <WordInput
                          currentICLId={currentICLId}
                          handleChangeTaskByKey={handleChangeTaskByKey}
                          id={id}
                          remoteTaskId={remoteTaskId}
                          wordUrl={wordUrl}
                          wordFile={wordFile}
                          setWordFile={setWordFile}
                          hasText
                          closeModal={closeFilesModal}
                        />
                      </>
                    )}
                  </div>
                </div>

                <div className="hidden items-center !gap-2 xl:flex xl:!gap-3">
                  <VideoInput
                    currentICLId={currentICLId}
                    handleChangeTaskByKey={handleChangeTaskByKey}
                    id={id}
                    remoteTaskId={remoteTaskId}
                    videoUrl={videoUrl}
                    videoFile={videoFile}
                    setVideoFile={setVideoFile}
                  />

                  <Tooltip
                    title={t('add-photo')}
                    componentsProps={{
                      popper: {
                        sx: {
                          ['& .MuiTooltip-arrow']: {
                            color: '#9CDFFC',
                          },
                          ['& .MuiTooltip-tooltip']: {
                            backgroundColor: '#9CDFFC',
                            color: '#000000',
                            fontSize: '14px',
                            padding: '14px',
                            borderRadius: '8px',
                          },
                        },
                      },
                    }}
                    arrow
                  >
                    <button
                      className="btn more small h-[40px] w-[40px] rounded-[8px] !p-[6px]"
                      onClick={() => {
                        imgInputRef.current?.click();
                      }}
                    >
                      <img src={imageSvg} alt="image" />

                      <input
                        className="hidden"
                        id="imageInput"
                        type="file"
                        ref={imgInputRef}
                        accept="image/*"
                        onChange={() => (remoteTaskId ? handleAddImage() : handleAddImageLocal())}
                      />
                    </button>
                  </Tooltip>

                  <AudioInput
                    currentICLId={currentICLId}
                    handleChangeTaskByKey={handleChangeTaskByKey}
                    id={id}
                    remoteTaskId={remoteTaskId}
                    audioUrl={audioUrl}
                    audioFile={audioFile}
                    setAudioFile={setAudioFile}
                  />

                  {workType === WorkType.hw && (
                    <>
                      <PdfInput
                        currentICLId={currentICLId}
                        handleChangeTaskByKey={handleChangeTaskByKey}
                        id={id}
                        remoteTaskId={remoteTaskId}
                        pdfUrl={pdfUrl}
                        pdfFile={pdfFile}
                        setPdfFile={setPdfFile}
                      />

                      <WordInput
                        currentICLId={currentICLId}
                        handleChangeTaskByKey={handleChangeTaskByKey}
                        id={id}
                        remoteTaskId={remoteTaskId}
                        wordUrl={wordUrl}
                        wordFile={wordFile}
                        setWordFile={setWordFile}
                      />
                    </>
                  )}
                </div>

                <div className="flex items-center !gap-2 lg:!gap-3">
                  <Tooltip
                    title={isActive ? t('deactivateTask') : t('activateTask')}
                    componentsProps={{
                      popper: {
                        sx: {
                          ['& .MuiTooltip-arrow']: {
                            color: '#9CDFFC',
                          },
                          ['& .MuiTooltip-tooltip']: {
                            backgroundColor: '#9CDFFC',
                            color: '#000000',
                            fontSize: '14px',
                            padding: '14px',
                            borderRadius: '8px',
                          },
                        },
                      },
                    }}
                    arrow
                  >
                    <button
                      className="btn more small h-[40px] w-[40px] rounded-[8px]"
                      onClick={() => handleChangeTaskByKey(id, 'isActive', !isActive)}
                    >
                      <span className="icon text-black">
                        {isActive ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
                      </span>
                    </button>
                  </Tooltip>

                  <Tooltip
                    title={t('deleteTask')}
                    componentsProps={{
                      popper: {
                        sx: {
                          ['& .MuiTooltip-arrow']: {
                            color: '#9CDFFC',
                          },
                          ['& .MuiTooltip-tooltip']: {
                            backgroundColor: '#9CDFFC',
                            color: '#000000',
                            fontSize: '14px',
                            padding: '14px',
                            borderRadius: '8px',
                          },
                        },
                      },
                    }}
                    arrow
                  >
                    <button
                      className="btn more small h-[40px] w-[40px] rounded-[8px] !p-[6px] text-red-600"
                      onClick={() => setIsModalOpen(true)}
                    >
                      <img src={trashSvg} alt="mic" />
                    </button>
                  </Tooltip>
                </div>
              </div>

              {isReadyToSaveExcludeTask() !== null ? (
                <button
                  type="button"
                  className="btn btn-primary moreStatic w-full grow !text-base font-bold lg:w-auto"
                  disabled={!formikProp.isValid || isSubmitButtonDisabled || (isReadyToSaveExcludeTask() as boolean)}
                  onClick={() => handleSave()}
                >
                  {t('save')}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary moreStatic w-full grow !text-base font-bold lg:w-auto"
                  disabled={!formikProp.isValid || isSubmitButtonDisabled}
                  onClick={() => handleSave()}
                >
                  {t('save')}
                </button>
              )}
            </div>
          </div>
          <ItemActionModal
            isOpen={isModalOpen}
            close={() => setIsModalOpen(false)}
            title={'deleteTask'}
            onHandle={() => handleDelete(id)?.then(() => toast.success(t('task successfully removed')))}
            isSubmitButtonDisabled={isDeleteButtonDisabled}
            buttonTitle="delete"
            warning="deleteTaskWarning"
          />
          {wordUrl && (
            <DocumentItem
              id={id}
              currentICLId={currentICLId}
              handleChangeTaskByKey={handleChangeTaskByKey}
              remoteTaskId={remoteTaskId}
              setFile={setWordFile}
              url={wordUrl}
              type={'doc'}
              downloadFile={downloadFile}
            />
          )}
          {pdfUrl && (
            <DocumentItem
              id={id}
              currentICLId={currentICLId}
              handleChangeTaskByKey={handleChangeTaskByKey}
              remoteTaskId={remoteTaskId}
              setFile={setPdfFile}
              url={pdfUrl}
              type={'pdf'}
              downloadFile={downloadFile}
            />
          )}
          {(audioUrl || audioFile) && (
            <AudioPlayer src={audioUrl ?? (audioFile?.url as string)} className="hidden lg:block" />
          )}
          {(videoUrl || videoFile) && (
            <VideoPlayer src={videoUrl ?? (videoFile?.url as string)} className="hidden w-1/2 lg:block" />
          )}
        </div>

        {deadline && <TaskDeadline />}
      </div>
      <ItemActionModal
        isOpen={isDeleteImgOpen}
        close={closeDeleteImg}
        title={'deletePhoto'}
        onHandle={() => handleDeleteImage(imageForDelete)}
        isSubmitButtonDisabled={false}
        buttonTitle={'delete'}
      />
      <BigPictureModal isOpen={isBigPictureOpen} onClose={bigPictureCLose} img={bigPicture} />
    </>
  );
}
